import * as React from "react";
import { AlertDialog } from "@/components/designSystem";

export interface ConfirmDialogConfig {
  /** 다이얼로그 제목 */
  title: string;
  /** 다이얼로그 설명 (선택) — 강조 색상 등이 필요하면 ReactNode 로 전달 */
  description?: React.ReactNode;
  /** 확인 버튼 텍스트 */
  confirmText?: string;
  /** 취소 버튼 텍스트 */
  cancelText?: string;
  /** 스타일 변형 */
  variant?: "primary" | "danger";
}

export interface UseConfirmDialogReturn {
  /** 다이얼로그 열기 상태 */
  isOpen: boolean;
  /** 다이얼로그 열기 (onConfirm 콜백 전달) */
  open: (onConfirm: () => void | Promise<void>) => void;
  /** 다이얼로그 닫기 */
  close: () => void;
  /** 다이얼로그 컴포넌트 (JSX에 렌더링) */
  ConfirmDialog: React.FC;
}

/**
 * 확인 다이얼로그를 위한 커스텀 훅
 *
 * @example
 * ```tsx
 * const deleteDialog = useConfirmDialog({
 *   title: "삭제할까요?",
 *   description: "삭제된 항목은 복구할 수 없습니다.",
 *   confirmText: "삭제",
 *   variant: "danger",
 * });
 *
 * // 다이얼로그 열기
 * deleteDialog.open(() => {
 *   // 확인 버튼 클릭 시 실행
 *   handleDelete();
 * });
 *
 * // JSX에서 렌더링
 * return (
 *   <>
 *     <button onClick={() => deleteDialog.open(handleDelete)}>삭제</button>
 *     <deleteDialog.ConfirmDialog />
 *   </>
 * );
 * ```
 */
export function useConfirmDialog(config: ConfirmDialogConfig): UseConfirmDialogReturn {
  const [isOpen, setIsOpen] = React.useState(false);
  const onConfirmRef = React.useRef<(() => void | Promise<void>) | null>(null);

  const open = React.useCallback((onConfirm: () => void | Promise<void>) => {
    onConfirmRef.current = onConfirm;
    setIsOpen(true);
  }, []);

  const close = React.useCallback(() => {
    setIsOpen(false);
    onConfirmRef.current = null;
  }, []);

  const handleConfirm = React.useCallback(async () => {
    if (onConfirmRef.current) {
      await onConfirmRef.current();
    }
    close();
  }, [close]);

  const ConfirmDialog = React.useCallback(() => (
    <AlertDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      title={config.title}
      description={config.description}
      confirmText={config.confirmText ?? "확인"}
      cancelText={config.cancelText ?? "취소"}
      onConfirm={handleConfirm}
      variant={config.variant ?? "primary"}
    />
  ), [isOpen, config, handleConfirm]);

  return {
    isOpen,
    open,
    close,
    ConfirmDialog,
  };
}

/**
 * 여러 확인 다이얼로그를 관리하는 훅
 *
 * @example
 * ```tsx
 * const dialogs = useConfirmDialogs({
 *   delete: {
 *     title: "삭제할까요?",
 *     variant: "danger",
 *   },
 *   save: {
 *     title: "저장할까요?",
 *     variant: "primary",
 *   },
 * });
 *
 * // 다이얼로그 열기
 * dialogs.open("delete", handleDelete);
 *
 * // JSX에서 렌더링
 * return <dialogs.ConfirmDialogs />;
 * ```
 */
export function useConfirmDialogs<T extends Record<string, ConfirmDialogConfig>>(
  configs: T
): {
  open: (key: keyof T, onConfirm: () => void | Promise<void>) => void;
  close: (key: keyof T) => void;
  isOpen: (key: keyof T) => boolean;
  ConfirmDialogs: React.FC;
} {
  const [openStates, setOpenStates] = React.useState<Record<keyof T, boolean>>(
    () => Object.keys(configs).reduce((acc, key) => ({ ...acc, [key]: false }), {} as Record<keyof T, boolean>)
  );
  const onConfirmRefs = React.useRef<Record<keyof T, (() => void | Promise<void>) | null>>(
    Object.keys(configs).reduce((acc, key) => ({ ...acc, [key]: null }), {} as Record<keyof T, (() => void | Promise<void>) | null>)
  );

  const open = React.useCallback((key: keyof T, onConfirm: () => void | Promise<void>) => {
    onConfirmRefs.current[key] = onConfirm;
    setOpenStates((prev) => ({ ...prev, [key]: true }));
  }, []);

  const close = React.useCallback((key: keyof T) => {
    setOpenStates((prev) => ({ ...prev, [key]: false }));
    onConfirmRefs.current[key] = null;
  }, []);

  const isOpen = React.useCallback((key: keyof T) => openStates[key], [openStates]);

  const ConfirmDialogs = React.useCallback(() => (
    <>
      {(Object.keys(configs) as (keyof T)[]).map((key) => {
        const config = configs[key];
        return (
          <AlertDialog
            key={String(key)}
            open={openStates[key]}
            onOpenChange={(open) => {
              if (!open) close(key);
            }}
            title={config.title}
            description={config.description}
            confirmText={config.confirmText ?? "확인"}
            cancelText={config.cancelText ?? "취소"}
            onConfirm={async () => {
              if (onConfirmRefs.current[key]) {
                await onConfirmRefs.current[key]!();
              }
              close(key);
            }}
            variant={config.variant ?? "primary"}
          />
        );
      })}
    </>
  ), [openStates, configs, close]);

  return {
    open,
    close,
    isOpen,
    ConfirmDialogs,
  };
}
